import { useState, useMemo } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  DrawerHeader,
  Flex,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { skipToken } from '@tanstack/react-query';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { CourseSegment, SkillSetContractCategories } from '@duke-hq/shared-constants/enrolments';
import { calculateEnrolmentMetrics } from '@duke-hq/svc-api/lib/enrolment/calculateEnrolmentMetrics';
import ActivityTracker from '@duke-hq/enrolments-ui/components/EnrolmentDetails/ActivityTracker';
import AssessmentsInRevision from '@duke-hq/enrolments-ui/components/EnrolmentDetails/AssessmentsInRevision';
import EnrolmentMetadata from '@duke-hq/enrolments-ui/components/EnrolmentDetails/EnrolmentMetadata';
import EnrolmentProgress from '@duke-hq/enrolments-ui/components/EnrolmentDetails/EnrolmentProgress';
import StudentDetails from '@duke-hq/enrolments-ui/components/EnrolmentDetails/StudentDetails';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import AddSemesterModal from './AddSemesterModal';
import DeleteEnrolmentModal from './DeleteEnrolmentModal';
import PauseEnrolmentModal from './PauseEnrolmentModal';
import { UpdateEnrolmentModal } from './UpdateEnrolment/UpdateEnrolmentModal';
import { TransferToSupersedingCourseModal } from './TransferToSupersedingCourseModal';
import WithdrawEnrolmentModal from './WithdrawEnrolmentModal';
import RestoreEnrolmentModal from './RestoreEnrolmentModal';
import type { AssessmentInRevision } from '@duke-hq/svc-api/lib/assessment/types';
import EnrolmentDocumentsModal from '@duke-hq/enrolments-ui/components/EnrolmentDocumentsModal';
import { listAvailableEnrolmentDocuments } from '@duke-hq/svc-api/lib/enrolment/listAvailableEnrolmentDocuments';
import { djs } from '@duke-hq/djs';
import { useEnrolmentDocumentQuery } from '../lib/useEnrolmentDocumentQuery';
import { EnrolmentActionsMenu } from './EnrolmentActionsMenu';
import { CourseStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseStatus.schema';
import { CourseSupersessionEventTypeSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseSupersessionEventType.schema';
import { CourseKindSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';
import {
  StudentCourseEnrolmentStatusSchema,
  type StudentCourseEnrolmentStatus,
} from '@duke-hq/svc-api-db/schemas/enums/StudentCourseEnrolmentStatus.schema';
import { ACTIVE_ENROLMENT_STATUSES } from '@duke-hq/svc-api/lib/enrolment/constants';

type EnrolmentHeaderProps = {
  enrolment: RouterOutputs['enrolment']['byIdWithResourceStatus'];
  assessmentsInRevision?: AssessmentInRevision[];
  onMarkAllWpComplete?: () => void;
};

export function EnrolmentHeader({
  enrolment,
  assessmentsInRevision,
  onMarkAllWpComplete,
}: EnrolmentHeaderProps): React.ReactElement {
  const [isUpdatingEnrolment, setIsUpdatingEnrolment] = useState<boolean>(false);
  const [isAddingSemester, setIsAddingSemester] = useState<boolean>(false);
  const [isWithdrawEnrolment, setIsWithdrawEnrolment] = useState<boolean>(false);
  const [isRestoreEnrolment, setIsRestoreEnrolment] = useState<boolean>(false);
  const [isPauseEnrolment, setIsPauseEnrolment] = useState<boolean>(false);
  const [isDeleteEnrolment, setIsDeleteEnrolment] = useState<boolean>(false);
  const [isEnrolmentDocument, setIsEnrolmentDocument] = useState<boolean>(false);
  const [isTransferEnrolment, setIsTransferEnrolment] = useState<boolean>(false);

  const getPdfDocumentDataFn = useEnrolmentDocumentQuery();

  const { user, id, course, status, course_segment } = enrolment;

  // Calculate metrics once
  const enrolmentMetrics = calculateEnrolmentMetrics(enrolment);
  const { currentSemester } = enrolmentMetrics;

  // Get weekly activity tracking
  const { data: weeks } = trpcAdmin.enrolment.getWeeklyActivityAndContacts.useQuery({
    courseEnrolmentId: enrolment.id,
    semester: currentSemester ?? 1,
  });

  // Query supersession history when course is superseded
  const { data: supersededCourse } = trpcAdmin.courses.courseSupersessionHistory.useQuery(
    course?.status === CourseStatusSchema.enum.SUPERSEDED ? { courseId: course.id } : skipToken,
  );

  const canAddSemester =
    course_segment === (CourseSegment.FIRST_SEMESTER_ONLY as string) &&
    status !== StudentCourseEnrolmentStatusSchema.enum.WITHDRAWN;
  const isActiveEnrolment = (ACTIVE_ENROLMENT_STATUSES as readonly string[]).includes(status);

  const canTransferToSupersedingCourse =
    (isActiveEnrolment || status === StudentCourseEnrolmentStatusSchema.enum.WITHDRAWN) &&
    supersededCourse != null &&
    supersededCourse.supersededByCourse != null &&
    supersededCourse.eventType === CourseSupersessionEventTypeSchema.enum.SUPERSEDED_TOGGLED_ON;

  const canDelete =
    status !== StudentCourseEnrolmentStatusSchema.enum.DELETED &&
    status !== StudentCourseEnrolmentStatusSchema.enum.COMPLETED;

  const validPausedStatuses: Array<StudentCourseEnrolmentStatus> = [
    StudentCourseEnrolmentStatusSchema.enum.PAUSED,
    StudentCourseEnrolmentStatusSchema.enum.IN_PROGRESS,
  ];

  const isSkillSet = useMemo(
    () => enrolment.course?.kind === CourseKindSchema.enum.SKILL_SET,
    [enrolment.course?.kind],
  );

  return (
    <DrawerHeader
      backgroundColor="bg.subtle"
      w="full"
      p={4}
      borderBottomWidth={1}
      borderColor="border.default"
    >
      {/* Header with title and actions menu */}
      <Flex justifyContent="space-between">
        <Text fontWeight="semibold" fontSize="2xl" mb={2}>
          {enrolment.contract_category ===
          String(SkillSetContractCategories.INTERNAL_ENROLMENTS) ? (
            <Trans>Internal Enrolment details</Trans>
          ) : (
            <Trans>Enrolment details</Trans>
          )}
        </Text>
        <Box marginRight={10} marginTop={-2}>
          <EnrolmentActionsMenu
            enrolment={enrolment}
            variant="header"
            onUpdateEnrolment={() => setIsUpdatingEnrolment(true)}
            onWithdrawEnrolment={() => setIsWithdrawEnrolment(true)}
            onRestoreEnrolment={() => setIsRestoreEnrolment(true)}
            onPauseResumeEnrolment={() => setIsPauseEnrolment(true)}
            onViewDocuments={() => setIsEnrolmentDocument(true)}
            onTransferToSupersedingCourse={
              canTransferToSupersedingCourse ? () => setIsTransferEnrolment(true) : undefined
            }
            onAddSemester={canAddSemester ? () => setIsAddingSemester(true) : undefined}
            onDeleteEnrolment={canDelete ? () => setIsDeleteEnrolment(true) : undefined}
            onMarkAllWpComplete={onMarkAllWpComplete}
          />
        </Box>
      </Flex>

      <EnrolmentMetadata
        course={
          course != null
            ? {
                title: course.title,
                identifier: course.identifier,
                accredited: course.accredited,
                status: CourseStatusSchema.parse(course.status),
              }
            : null
        }
        enrolmentMetrics={enrolmentMetrics}
        enrolment={enrolment}
        tenantName={enrolment.tenant?.name}
        isSkillSet={isSkillSet}
        consultantHref={
          enrolment.consultant_id != null && enrolment.tenant_id != null
            ? `/providers/${enrolment.tenant_id}/consultants/${enrolment.consultant_id}/details`
            : undefined
        }
        consultantEmail={
          'consultant' in enrolment ? (enrolment.consultant?.email ?? undefined) : undefined
        }
        consultantPhone={
          'consultant' in enrolment ? (enrolment.consultant?.phone_number ?? undefined) : undefined
        }
        placementHref={
          enrolment.workPlacementJourneyStatus != null
            ? `/placements/students/${enrolment.id}`
            : undefined
        }
        labels={{
          accredited: t`Accredited`,
          consultant: t`Consultant:`,
          segment: t`Segment`,
          pace: t`Pace`,
          fullTime: t`Full-time`,
          partTime: t`Part-time`,
          startDate: t`Start date`,
          firstActivity: t`First activity`,
          targetDate: t`Target date`,
          contract: t`Contract`,
          placements: t`View placements`,
        }}
      />

      {supersededCourse != null &&
      supersededCourse.supersededByCourse != null &&
      supersededCourse.eventType === CourseSupersessionEventTypeSchema.enum.SUPERSEDED_TOGGLED_ON &&
      isActiveEnrolment ? (
        <VStack w="full" gap="8px" alignItems="right" justifyContent="center" mt={3}>
          <Alert status="warning" borderRadius="md">
            <AlertIcon alignSelf="flex-start" mt="2px" />
            <AlertDescription>
              <VStack align="start" spacing="0px" gap="0px">
                <Text fontSize="sm" lineHeight="1.5">
                  {canAddSemester ? (
                    <Trans>
                      This course has been superseded. Enrolment in Second Semester will require a
                      transfer to the new{' '}
                      <Text as="span" fontWeight="bold">
                        {supersededCourse.supersededByCourse.identifier != null
                          ? supersededCourse.supersededByCourse.identifier
                          : ''}
                      </Text>{' '}
                      course
                    </Trans>
                  ) : (
                    <Trans>
                      This course has been superseded by{' '}
                      <Text as="span" fontWeight="bold">
                        {supersededCourse.supersededByCourse.identifier != null
                          ? supersededCourse.supersededByCourse.identifier
                          : ''}
                      </Text>{' '}
                      course
                    </Trans>
                  )}
                </Text>
              </VStack>
            </AlertDescription>
          </Alert>
        </VStack>
      ) : null}

      <Flex backgroundColor="bg.subtle" w="full" gap={3} mt={3}>
        <StudentDetails
          user={user}
          labels={{
            learner: t`Learner`,
            student: t`Student`,
            viewProfile: t`View profile`,
            copyEmail: t`Copy email address`,
            deleted: t`Deleted`,
          }}
        />
        <EnrolmentProgress
          enrolment={enrolment}
          enrolmentMetrics={enrolmentMetrics}
          isSkillSet={isSkillSet}
          labels={{
            progress: t`Progress`,
            semester: t`Semester`,
            placement: t`Placement`,
          }}
        />
      </Flex>

      {/* Weekly Activity Tracking and Assessment Revision */}
      <Flex backgroundColor="bg.subtle" w="full" gap={3} mt={3}>
        <ActivityTracker
          weeks={weeks}
          currentSemester={
            enrolment.course?.kind === CourseKindSchema.enum.QUALIFICATION ? currentSemester : null
          }
          labels={{
            noStudentDetails: t`No student details.`,
            unableToContact: t`Unable to contact.`,
            activity: t`Activity`,
            contacts: t`Contacts`,
          }}
        />
        {assessmentsInRevision !== undefined ? (
          <AssessmentsInRevision
            assessmentsInRevision={assessmentsInRevision}
            labels={{ title: t`Assessments in revision` }}
          />
        ) : null}
      </Flex>

      {/* Modals */}
      {canTransferToSupersedingCourse && supersededCourse?.supersededByCourse != null ? (
        <TransferToSupersedingCourseModal
          enrolmentId={enrolment.id}
          isOpen={isTransferEnrolment}
          onClose={() => setIsTransferEnrolment(false)}
          supersededCourse={supersededCourse}
        />
      ) : null}

      {canAddSemester ? (
        <AddSemesterModal
          enrolment={enrolment}
          isOpen={isAddingSemester}
          onClose={() => setIsAddingSemester(false)}
          supersededCourse={supersededCourse}
        />
      ) : null}

      <UpdateEnrolmentModal
        enrolmentId={enrolment.id}
        isOpen={isUpdatingEnrolment}
        onClose={() => setIsUpdatingEnrolment(false)}
      />

      {status !== StudentCourseEnrolmentStatusSchema.enum.WITHDRAWN ? (
        <WithdrawEnrolmentModal
          enrolment={enrolment}
          isOpen={isWithdrawEnrolment}
          onClose={() => setIsWithdrawEnrolment(false)}
        />
      ) : null}

      {status === StudentCourseEnrolmentStatusSchema.enum.WITHDRAWN ? (
        <RestoreEnrolmentModal
          enrolmentId={id}
          studentId={user.id}
          isOpen={isRestoreEnrolment}
          onClose={() => setIsRestoreEnrolment(false)}
        />
      ) : null}

      {isPauseEnrolment && validPausedStatuses.includes(status) ? (
        <PauseEnrolmentModal
          enrolment={enrolment}
          isOpen={isPauseEnrolment}
          onClose={() => {
            setIsPauseEnrolment(false);
          }}
        />
      ) : null}

      {status !== StudentCourseEnrolmentStatusSchema.enum.DELETED ? (
        <DeleteEnrolmentModal
          enrolment={enrolment}
          isOpen={isDeleteEnrolment}
          onClose={() => setIsDeleteEnrolment(false)}
        />
      ) : null}

      <EnrolmentDocumentsModal
        enrolment={enrolment}
        isOpen={isEnrolmentDocument}
        onClose={() => setIsEnrolmentDocument(false)}
        availableDocuments={listAvailableEnrolmentDocuments({
          enrolment,
          isAdmin: true,
          userUTCOffset: djs().utcOffset(),
        })}
        getPdfDocumentDataFn={getPdfDocumentDataFn}
        labels={{
          title: t`Enrolment Documents`,
          emptyMessage: t`This enrolment doesn't have any documents available at the moment.`,
          view: t`View`,
          done: t`Done`,
        }}
      />
    </DrawerHeader>
  );
}
